import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { Popup } from "../Popup";
import { useStore } from "../../store/globalState";
import {
  getNotificationPreferences,
  getSmsConsent,
  saveNotificationPreferences,
  type NotificationPreferenceChannel,
} from "../../services/api";
import { useSmsConsentStore } from "../../store/useSmsConsentStore";
import { smsSwitchNeedsPrompt } from "./smsConsentPrompt";
import "./NotificationsDrawer.css";
import { CloseLight } from "../../assets/navIcons";

const CHANNEL_LABEL: Record<NotificationPreferenceChannel["channel"], string> = {
  in_app: "notifications.channelInApp",
  email: "notifications.channelEmail",
  sms: "notifications.channelSms",
};

export function NotificationPreferencesSheet({
  open,
  linkId,
  onClose,
  onRequestSmsConsent,
}: {
  open: boolean;
  linkId: string | null;
  onClose: () => void;
  /**
   * Close the sheet. The consent popup is already pending, except when this
   * version already has a Yes: then the switch just saves.
   */
  onRequestSmsConsent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { backgroundBtn, bodyCopy } = useStore().theme.whiteView;
  const [channels, setChannels] = useState<NotificationPreferenceChannel[]>([]);
  const [failed, setFailed] = useState(false);
  const channelsRef = useRef<NotificationPreferenceChannel[]>([]);
  const confirmedRef = useRef<NotificationPreferenceChannel[]>([]);
  const desiredRef = useRef<NotificationPreferenceChannel[] | null>(null);
  const savingRef = useRef(false);
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!open || !linkId) return;
    const session = ++sessionRef.current;
    let cancelled = false;
    setFailed(false);
    setChannels([]);
    channelsRef.current = [];
    void getNotificationPreferences(linkId)
      .then((next) => {
        if (cancelled || session !== sessionRef.current) return;
        confirmedRef.current = next;
        channelsRef.current = next;
        desiredRef.current = null;
        setChannels(next);
      })
      .catch(() => {
        if (!cancelled && session === sessionRef.current) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, linkId]);

  if (!open) return null;

  async function flush(id: string) {
    if (savingRef.current) return;
    savingRef.current = true;
    const session = sessionRef.current;
    try {
      while (desiredRef.current && session === sessionRef.current) {
        const snapshot = desiredRef.current;
        desiredRef.current = null;
        try {
          const saved = await saveNotificationPreferences(id, snapshot);
          if (session !== sessionRef.current) return;
          confirmedRef.current = saved;
          if (desiredRef.current) continue;
          channelsRef.current = saved;
          setChannels(saved);
        } catch {
          if (session !== sessionRef.current) return;
          if (desiredRef.current) continue;
          channelsRef.current = confirmedRef.current;
          setChannels(confirmedRef.current);
          break;
        }
      }
    } finally {
      savingRef.current = false;
      if (desiredRef.current) void flush(id);
    }
  }

  function applyToggle(channel: NotificationPreferenceChannel["channel"]) {
    if (!linkId) return;
    const next = channelsRef.current.map((row) =>
      row.channel === channel ? { ...row, enabled: !row.enabled } : row,
    );
    channelsRef.current = next;
    desiredRef.current = next;
    setChannels(next);
    void flush(linkId);
  }

  async function turnSmsOn() {
    if (!linkId) return;
    try {
      const consent = await getSmsConsent(
        linkId,
        i18n.language?.slice(0, 2) || "en",
      );
      if (!consent.available) return;
      if (smsSwitchNeedsPrompt(consent)) {
        useSmsConsentStore.getState().setPending(consent, { source: "preferences" });
        onRequestSmsConsent();
        return;
      }
    } catch {
      return;
    }
    applyToggle("sms");
  }

  function toggle(channel: NotificationPreferenceChannel["channel"]) {
    const current = channelsRef.current.find((row) => row.channel === channel);
    if (channel === "sms" && current && !current.enabled) {
      void turnSmsOn();
      return;
    }
    applyToggle(channel);
  }

  return (
    <Popup visible={open} onClose={onClose}>
      <section
        className="notifications-sheet"
        role="dialog"
        aria-label={t("notifications.preferencesTitle")}
      >
        <header className="payout-details-header">
          <span className="payout-header-action" aria-hidden="true" />
          <Title
            title={t("notifications.preferencesTitle")}
            align="center"
            color={bodyCopy}
          />
          <button
            type="button"
            className="payout-header-action payout-close-btn"
            aria-label={t("notifications.close")}
            onClick={onClose}
          >
            <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
          </button>
        </header>
        {failed ? (
          <p className="notifications-sheet-empty" style={{ color: bodyCopy }}>
            {t("notifications.preferencesError")}
          </p>
        ) : (
          <ul className="notifications-pref-list">
            {channels.map((row) => (
              <li key={row.channel}>
                <span style={{ color: bodyCopy }}>
                  {t(CHANNEL_LABEL[row.channel])}
                </span>
                <button
                  type="button"
                  role="switch"
                  className="notifications-pref-switch"
                  aria-checked={row.enabled}
                  aria-label={t(CHANNEL_LABEL[row.channel])}
                  style={{ "--bg": backgroundBtn } as CSSProperties}
                  onClick={() => void toggle(row.channel)}
                >
                  <span />
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="notifications-sheet-foot" style={{ color: bodyCopy }}>
          {t("notifications.preferencesEssential")}
        </p>
      </section>
    </Popup>
  );
}
